/**
 * Performance monitoring and optimization utilities
 */

// Performance metrics collection
export interface PerformanceMetrics {
  pageLoadTime: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
  cumulativeLayoutShift: number;
  firstInputDelay: number;
  timeToInteractive: number;
  bundleSize: number;
  apiResponseTimes: Record<string, number>;
}

// Performance monitoring class
export class PerformanceMonitor {
  private metrics: Partial<PerformanceMetrics> = {};
  private apiTimings: Record<string, number> = {};

  constructor() {
    this.initializeWebVitals();
    this.monitorApiCalls();
  }

  private initializeWebVitals() {
    // Monitor Core Web Vitals
    if (typeof window !== 'undefined' && 'performance' in window) {
      // First Contentful Paint
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            this.metrics.firstContentfulPaint = entry.startTime;
          }
        }
      }).observe({ entryTypes: ['paint'] });

      // Largest Contentful Paint
      new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.metrics.largestContentfulPaint = lastEntry.startTime;
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // Cumulative Layout Shift
      new PerformanceObserver((list) => {
        let clsValue = 0;
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        this.metrics.cumulativeLayoutShift = clsValue;
      }).observe({ entryTypes: ['layout-shift'] });

      // First Input Delay
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.metrics.firstInputDelay = (entry as any).processingStart - entry.startTime;
        }
      }).observe({ entryTypes: ['first-input'] });
    }
  }

  private monitorApiCalls() {
    // Intercept fetch calls to monitor API performance
    if (typeof window !== 'undefined') {
      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const startTime = performance.now();
        const url = typeof args[0] === 'string' ? args[0] : args[0].url;
        
        try {
          const response = await originalFetch(...args);
          const endTime = performance.now();
          const duration = endTime - startTime;
          
          // Store API timing
          this.apiTimings[url] = duration;
          
          return response;
        } catch (error) {
          const endTime = performance.now();
          const duration = endTime - startTime;
          this.apiTimings[`${url}_error`] = duration;
          throw error;
        }
      };
    }
  }

  public getMetrics(): Partial<PerformanceMetrics> {
    return {
      ...this.metrics,
      apiResponseTimes: this.apiTimings,
      pageLoadTime: performance.now()
    };
  }

  public logMetrics() {
    const metrics = this.getMetrics();
    console.group('🚀 Performance Metrics');
    console.log('Page Load Time:', `${metrics.pageLoadTime?.toFixed(2)}ms`);
    console.log('First Contentful Paint:', `${metrics.firstContentfulPaint?.toFixed(2)}ms`);
    console.log('Largest Contentful Paint:', `${metrics.largestContentfulPaint?.toFixed(2)}ms`);
    console.log('Cumulative Layout Shift:', metrics.cumulativeLayoutShift?.toFixed(4));
    console.log('First Input Delay:', `${metrics.firstInputDelay?.toFixed(2)}ms`);
    console.log('API Response Times:', metrics.apiResponseTimes);
    console.groupEnd();
  }

  public reportToAnalytics(metrics: Partial<PerformanceMetrics>) {
    // Send metrics to analytics service
    if (import.meta.env.PROD) {
      // In production, send to your analytics service
      console.log('Sending metrics to analytics:', metrics);
    }
  }
}

// Bundle size analyzer
export const analyzeBundleSize = () => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    const entries = performance.getEntriesByType('navigation');
    if (entries.length > 0) {
      const entry = entries[0] as PerformanceNavigationTiming;
      return {
        transferSize: entry.transferSize,
        encodedBodySize: entry.encodedBodySize,
        decodedBodySize: entry.decodedBodySize
      };
    }
  }
  return null;
};

// Memory usage monitoring
export const getMemoryUsage = () => {
  if ('memory' in performance) {
    return {
      usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
      totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
      jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit
    };
  }
  return null;
};

// Database query optimization helpers
export const optimizeQuery = (query: string, params: any[]) => {
  const startTime = performance.now();
  
  return {
    execute: async (queryFn: () => Promise<any>) => {
      try {
        const result = await queryFn();
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        console.log(`Query executed in ${duration.toFixed(2)}ms:`, query);
        
        return result;
      } catch (error) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        console.error(`Query failed after ${duration.toFixed(2)}ms:`, query, error);
        throw error;
      }
    }
  };
};

// Image optimization utilities
export const optimizeImage = (src: string, width?: number, height?: number) => {
  // For Pexels images, add optimization parameters
  if (src.includes('pexels.com')) {
    const url = new URL(src);
    if (width) url.searchParams.set('w', width.toString());
    if (height) url.searchParams.set('h', height.toString());
    url.searchParams.set('auto', 'compress');
    url.searchParams.set('cs', 'tinysrgb');
    return url.toString();
  }
  return src;
};

// Lazy loading utility
export const createLazyLoader = () => {
  if (typeof window === 'undefined') return null;
  
  return new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const img = entry.target as HTMLImageElement;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
      }
    });
  }, {
    rootMargin: '50px'
  });
};

// Initialize performance monitoring
export const initializePerformanceMonitoring = () => {
  if (typeof window !== 'undefined') {
    const monitor = new PerformanceMonitor();
    
    // Log metrics after page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        monitor.logMetrics();
        monitor.reportToAnalytics(monitor.getMetrics());
      }, 1000);
    });
    
    return monitor;
  }
  return null;
};